import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

import { userService } from '@/feature/user/services/userService';
import type { IUser } from '@/feature/user/types/user';
import { formatDate } from '@/shared/utils/dateUtils';

import { ROUTES } from '../../routes/routeConstants';
import StatusBadge from '../common/StatusBadge';

const formatLastLogin = (dateStr: string) => {
  if (!dateStr) {
    return '—';
  }
  const formatted = formatDate(dateStr);
  return formatted || dateStr;
};

const UserDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) {
      return;
    }
    userService.getUserById(id).then((data) => {
      if (!data) {
        setNotFound(true);
      } else {
        setUser(data);
      }
      setLoading(false);
    });
  }, [id]);

  if (loading) {
    return (
      <div className='px-6 pt-3 pb-6 bg-gray-50 min-h-full flex items-center justify-center'>
        <span className='text-gray-500'>Loading...</span>
      </div>
    );
  }

  if (notFound || !user) {
    return (
      <div className='px-6 pt-3 pb-6 bg-gray-50 min-h-full'>
        <nav className='flex items-center gap-1.5 text-sm mb-4' aria-label='Breadcrumb'>
          <Link to={ROUTES.DASHBOARD} className='text-theme-500 hover:opacity-80 flex items-center'>
            <svg
              width='16'
              height='16'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M3 9.5L10 3L17 9.5V17H13V13H7V17H3V9.5Z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinejoin='round'
              />
            </svg>
          </Link>
          <span className='text-gray-400'>›</span>
          <Link to={ROUTES.USERS} className='text-theme-500 hover:opacity-80'>
            Users
          </Link>
          <span className='text-gray-400'>›</span>
          <span className='text-gray-700'>Not Found</span>
        </nav>
        <div className='text-red-600'>User not found</div>
      </div>
    );
  }

  return (
    <div className='px-6 pt-3 pb-6 bg-gray-50 min-h-full'>
      <nav className='flex items-center gap-1.5 text-sm mb-6' aria-label='Breadcrumb'>
        <Link to={ROUTES.DASHBOARD} className='text-theme-500 hover:opacity-80 flex items-center'>
          <svg
            width='16'
            height='16'
            viewBox='0 0 20 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M3 9.5L10 3L17 9.5V17H13V13H7V17H3V9.5Z'
              stroke='currentColor'
              strokeWidth='1.5'
              strokeLinejoin='round'
            />
          </svg>
        </Link>
        <span className='text-gray-400'>›</span>
        <Link to={ROUTES.ORGANIZATIONS} className='text-theme-500 hover:opacity-80'>
          Organization Management
        </Link>
        <span className='text-gray-400'>›</span>
        <Link to={ROUTES.USERS} className='text-theme-500 hover:opacity-80'>
          Users
        </Link>
        <span className='text-gray-400'>›</span>
        <span className='text-gray-700'>{user.name}</span>
      </nav>

      <div className='mb-6'>
        <h1 className='text-3xl font-bold text-gray-900 m-0'>{user.name}</h1>
      </div>

      <div className='card mb-8'>
        <div className='flex flex-col gap-4 mb-6'>
          <div className='flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4'>
            <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide shrink-0'>
              EMAIL
            </span>
            <span className='text-gray-900'>{user.email}</span>
          </div>
          <div className='flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4'>
            <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide shrink-0'>
              LAST LOGIN
            </span>
            <span className='text-gray-900'>{formatLastLogin(user.lastLogin)}</span>
          </div>
          <div className='flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4'>
            <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide shrink-0'>
              ORGANIZATION
            </span>
            <span className='text-gray-900'>{(user.org as { org_name?: string })?.org_name}</span>
          </div>
          <div className='flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4'>
            <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide shrink-0'>
              ROLE
            </span>
            <span className='text-gray-900'>{user.role}</span>
          </div>
        </div>
        <div className='pt-4 border-t border-gray-100'>
          <div className='flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4'>
            <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>
              STATUS
            </span>
            <StatusBadge status={user.status as 'Active' | 'Inactive'} />
          </div>
        </div>
      </div>

      {/* <div className='card'>
        <h2 className='text-xl font-bold text-gray-900 m-0 mb-4'>
          Organizations ({organizations.length})
        </h2>
        <TanStackTable<IUserOrganization>
          data={organizations}
          columns={orgColumns}
          emptyMessage='No organizations found'
          enablePagination={false}
          searchPlaceholder='Search organizations...'
          getRowId={(row) => String(row.id)}
        />
      </div> */}
    </div>
  );
};

export default UserDetailsPage;
